// KeyboardShortcutsOverlay — global ``?`` help surface (plan §P2.19 —
// UI Workflow Consistency Audit).
//
// The Layout already had a Keyboard icon button that did nothing
// (audit §8.14). Replace the dead affordance with a real cheat-sheet
// modal listing every shortcut the app supports today, plus open it
// when the user presses ``?`` from anywhere outside an editable input.
//
// Today's shortcut surface is intentionally tiny — keyboard-driven
// power-use is a quarter goal (plan P3) — but having one place that
// catalogs them makes it obvious what does and doesn't exist, and
// gives us a hook to extend.

import { useEffect } from 'react'
import { Command, X } from 'lucide-react'

import { cn } from '@/lib/utils'

interface ShortcutEntry {
  /** Pre-rendered key glyph row (``["?"], ["G", "1"]`` etc.). */
  keys: string[][]
  description: string
  /** Optional context where the shortcut applies (Command Center, Hub…). */
  scope?: string
}

const SHORTCUTS: ShortcutEntry[] = [
  {
    keys: [['?']],
    description: 'Open this keyboard cheat sheet',
  },
  {
    keys: [['Esc']],
    description: 'Close any open dialog or this overlay',
  },
  {
    keys: [['G'], ['H']],
    description: 'Jump to the Projects Hub',
  },
  {
    keys: [['G'], ['I']],
    description: 'Start a Smart Intake (new project)',
  },
  {
    keys: [['['], [']']],
    description: 'Cycle the active focus on the Timeline rail',
    scope: 'Command Center',
  },
  {
    keys: [['1'], ['2'], ['3'], ['4'], ['5']],
    description: 'Jump to Phase 1–5 in the Timeline rail',
    scope: 'Command Center',
  },
  {
    keys: [['T']],
    description: 'Toggle dark / light theme',
  },
]

export interface KeyboardShortcutsOverlayProps {
  open: boolean
  onClose: () => void
}

export function KeyboardShortcutsOverlay({
  open,
  onClose,
}: KeyboardShortcutsOverlayProps) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm alm-fadein"
      onClick={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="alm-shortcuts-title"
        onClick={(e) => e.stopPropagation()}
        className="alm-card max-w-lg w-[92%] mx-4 shadow-2xl"
      >
        <div className="flex items-start gap-3 mb-3">
          <Command className="h-5 w-5 text-indigo-300 shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <div
              id="alm-shortcuts-title"
              className="text-base font-semibold text-slate-100"
            >
              Keyboard shortcuts
            </div>
            <div className="text-xs text-slate-400 mt-0.5">
              Press{' '}
              <kbd className="alm-kbd">?</kbd> from anywhere outside a text
              field to open this sheet.
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close shortcuts"
            className="rounded p-1 text-slate-400 hover:text-slate-100 hover:bg-white/5"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <ul className="space-y-2">
          {SHORTCUTS.map((row, i) => (
            <li key={i} className="flex items-center gap-3">
              <div className="flex items-center gap-1 min-w-[120px]">
                {row.keys.map((seq, j) => (
                  <span key={j} className="flex items-center gap-0.5">
                    {seq.map((k, kk) => (
                      <kbd key={kk} className="alm-kbd">
                        {k}
                      </kbd>
                    ))}
                    {j < row.keys.length - 1 ? (
                      <span className="text-slate-600 text-[10px] mx-0.5">
                        then
                      </span>
                    ) : null}
                  </span>
                ))}
              </div>
              <div className="text-[12.5px] text-slate-200 flex-1">
                {row.description}
                {row.scope ? (
                  <span
                    className={cn(
                      'alm-chip alm-chip-info ml-2 text-[10px] uppercase tracking-wider',
                    )}
                  >
                    {row.scope}
                  </span>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
        <p className="mt-4 text-[11px] text-slate-500">
          Editable inputs ignore shortcuts so you can type{' '}
          <kbd className="alm-kbd">?</kbd> inside the problem-statement field.
        </p>
      </div>
    </div>
  )
}
